import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Grid3x3,
  Mic,
  MicOff,
  Minus,
  Pause,
  PhoneOff,
  Play,
  Repeat,
  UserPlus,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useCallStore } from '@/stores/useCallStore'
import { estIdentiteAgent } from '@/lib/identiteAgent'
import { useCallActions } from '@/hooks/useCallActions'
import { useUIStore } from '@/stores/useUIStore'
import { useDraggable } from '@/hooks/useDraggable'
import { TransferPanel } from './TransferPanel'
import { AnnonceBar } from './AnnonceBar'

const WIDGET_POSITION_KEY = 'callizy:incall-widget-position'

/** Position initiale = bas-droite avec marge de 16px, taille approx 340x150. */
function getDefaultPosition() {
  if (typeof window === 'undefined') return { x: 0, y: 0 }
  return {
    x: Math.max(8, window.innerWidth - 340 - 16),
    y: Math.max(8, window.innerHeight - 150 - 16),
  }
}

interface Props {
  /** Si true, le widget s'ouvre en pilule (par défaut côté agent qui a quitté
   *  la timeline). Côté manager, on garde le compact par défaut. */
  defaultMinimized?: boolean
}

/**
 * Widget flottant de contrôle d'appel — visible pendant un appel connecté.
 *
 * Auto-caché si `useCallStore.hasInlineCallUI === true` (l'agent est sur la
 * timeline du contact, l'InCallPanel inline prend le relais). Permet à
 * l'agent de garder la main quand il quitte la page contact.
 *
 * 2 modes :
 *  - compact (~340px) : avatar, nom, numéro, timer, Mute/Hold/Keypad/Transfer/Hangup
 *  - minimized (pilule ~220px) : timer + nom + chevron pour expand
 */
export function InCallWidget({ defaultMinimized = false }: Props = {}) {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const [mounted, setMounted] = useState(false)
  const [minimized, setMinimized] = useState(defaultMinimized)
  const [showKeypad, setShowKeypad] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  // The inline panel opened by « Ajouter » rather than « Transfert ».
  const [ajout, setAjout] = useState(false)
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
  )
  const rootRef = useRef<HTMLDivElement>(null)

  const isCallConnected = useCallStore((s) => s.isCallConnected)
  const isCallMuted = useCallStore((s) => s.isCallMuted)
  const callDuration = useCallStore((s) => s.callDuration)
  const isOnHold = useCallStore((s) => s.isOnHold)
  // Announced transfer under way: hold and another transfer wait for its end.
  const enAnnonce = useCallStore((s) => !!s.annonce?.etape)
  const isHoldLoading = useCallStore((s) => s.isHoldLoading)
  const callerContactName = useCallStore((s) => s.callerContactName)
  const isInternalCall = useCallStore((s) => s.isInternalCall)
  const callerPhoneNumber = useCallStore((s) => s.callerPhoneNumber)
  const hasInlineCallUI = useCallStore((s) => s.hasInlineCallUI)
  // Call with several people: no other transfer; leaving keeps the others on the line.
  const participants = useCallStore((s) => s.participants)
  const ajoutEnCours = useCallStore((s) => s.ajoutEnCours)
  const quitte = participants.some((p) => p.en_ligne && !p.externe)

  const { toggleMute, hangUp, sendDTMF, hold, unhold } = useCallActions()

  // Drag — désactivé sous le breakpoint md (espace insuffisant)
  const { position, isDragging, didDrag, onPointerDown } = useDraggable({
    storageKey: WIDGET_POSITION_KEY,
    defaultPosition: getDefaultPosition,
    enabled: isDesktop,
  })

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Suivre les changements de breakpoint
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Reset l'état d'UI à chaque nouvel appel (sinon les panneaux restent ouverts)
  useEffect(() => {
    if (!isCallConnected) {
      setShowKeypad(false)
      setShowTransfer(false)
      setMinimized(defaultMinimized)
    }
  }, [isCallConnected, defaultMinimized])

  const cancelTransfer = () => {
    setShowTransfer(false)
  }

  const openTransferPanel = () => {
    setShowKeypad(false) // keypad et transfer s'excluent
    setAjout(false)
    setShowTransfer(true)
  }

  const openAddPanel = () => {
    setShowKeypad(false)
    setAjout(true)
    setShowTransfer(true)
  }

  const toggleKeypad = () => {
    setShowTransfer(false)
    setShowKeypad((v) => !v)
  }

  // Caché si une UI inline (InCallPanel sous timeline) est déjà visible
  if (!mounted || !isCallConnected || hasInlineCallUI) return null

  // Appel d'un collègue : jamais l'identité brute (client:agent_…) à l'écran.
  const interne = isInternalCall || estIdentiteAgent(callerPhoneNumber)
  const numeroAffiche = interne ? '' : callerPhoneNumber
  const displayName =
    callerContactName || numeroAffiche || (interne ? t('phone.internalCall', 'Appel interne') : t('phone.unknown', 'Inconnu'))
  const initials = getInitials(callerContactName, numeroAffiche)

  return createPortal(
    <div
      ref={rootRef}
      data-drag-root
      className={cn(
        'fixed z-[60]',
        // Mobile (< md) : position figée bas-droite. Desktop : position via style.
        isDesktop ? '' : 'bottom-4 right-4',
        isDragging && 'select-none',
      )}
      style={
        isDesktop
          ? {
              left: `${position.x}px`,
              top: `${position.y}px`,
              transition: isDragging ? 'none' : 'box-shadow 0.15s',
            }
          : undefined
      }
      role="region"
      aria-label={t('phone.inProgress', 'Appel en cours')}
    >
      <AnimatePresence mode="wait" initial={false}>
        {minimized ? (
          <motion.div
            key="pill"
            role="button"
            tabIndex={0}
            initial={{ opacity: 0, scale: 0.9, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 8 }}
            transition={{ duration: 0.15 }}
            onPointerDown={onPointerDown}
            onClick={() => {
              // Inhibe l'expand si le pointer a draggé (seuil 5px dans useDraggable)
              if (didDrag) return
              setMinimized(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setMinimized(false)
              }
            }}
            className={cn(
              'flex items-center gap-2 rounded-full shadow-2xl border pl-2 pr-1 py-1',
              'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              isDesktop && (isDragging ? 'cursor-grabbing' : 'cursor-grab'),
              darkMode
                ? 'bg-slate-800 border-slate-700 hover:bg-slate-700'
                : 'bg-white border-slate-200 hover:bg-slate-50',
            )}
            title={t('phone.expand', 'Agrandir')}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full ml-1',
                isOnHold ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse',
              )}
            />
            <span
              className={cn(
                'text-xs font-mono font-bold tabular-nums',
                darkMode ? 'text-emerald-400' : 'text-emerald-700',
              )}
            >
              {formatDuration(callDuration)}
            </span>
            <span
              className={cn(
                'text-xs font-medium truncate max-w-[120px]',
                darkMode ? 'text-slate-200' : 'text-slate-700',
              )}
            >
              {displayName}
            </span>
            {/* Hangup direct sans expand. stopPropagation pour ne pas
                déclencher l'onClick de la pilule (qui expand) ni le drag. */}
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                hangUp()
              }}
              disabled={isOnHold}
              className="h-7 w-7 rounded-full flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50 shadow-sm cursor-pointer"
              title={t('phone.hangup', 'Raccrocher')}
              aria-label={t('phone.hangup', 'Raccrocher')}
            >
              <PhoneOff className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="compact"
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'rounded-2xl shadow-2xl border w-[340px] overflow-hidden',
              darkMode
                ? 'bg-slate-900 border-slate-700'
                : 'bg-white border-slate-200',
            )}
          >
            {/* Header : statut + nom + timer + minimize.
                Le header (avatar + nom + timer) sert de poignée de drag.
                Le bouton minimize est exclu pour rester cliquable. */}
            <div
              className={cn(
                'flex items-center gap-3 px-4 py-3 border-b',
                darkMode
                  ? 'bg-emerald-500/10 border-slate-800'
                  : 'bg-emerald-50/60 border-slate-100',
              )}
            >
              <div
                onPointerDown={onPointerDown}
                className={cn(
                  'flex items-center justify-center h-9 w-9 rounded-full bg-emerald-500 flex-shrink-0',
                  isDesktop && (isDragging ? 'cursor-grabbing' : 'cursor-grab'),
                )}
              >
                <span className="text-white font-bold text-xs">{initials}</span>
              </div>
              <div
                onPointerDown={onPointerDown}
                className={cn(
                  'flex-1 min-w-0',
                  isDesktop && (isDragging ? 'cursor-grabbing' : 'cursor-grab'),
                )}
              >
                <p
                  className={cn(
                    'font-semibold text-sm truncate',
                    darkMode ? 'text-white' : 'text-slate-900',
                  )}
                >
                  {displayName}
                </p>
                {/* Numéro affiché sous le nom — masqué si le nom EST déjà
                    le numéro (pas de match contact côté backend). */}
                {callerContactName && (interne || callerPhoneNumber) && (
                  <p
                    className={cn(
                      'text-[11px] truncate tabular-nums',
                      darkMode ? 'text-slate-400' : 'text-slate-500',
                    )}
                  >
                    {interne ? t('phone.internalCall', 'Appel interne') : callerPhoneNumber}
                  </p>
                )}
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full flex-shrink-0',
                      isOnHold
                        ? 'bg-amber-500'
                        : 'bg-emerald-500 animate-pulse',
                    )}
                  />
                  <span
                    className={cn(
                      'text-[11px] font-medium',
                      darkMode ? 'text-emerald-400' : 'text-emerald-700',
                    )}
                  >
                    {isOnHold
                      ? t('phone.onHold', 'En attente')
                      : t('phone.inProgress', 'Appel en cours')}
                  </span>
                  <span
                    className={cn(
                      'text-[11px] font-mono font-bold tabular-nums ml-auto',
                      darkMode ? 'text-emerald-400' : 'text-emerald-700',
                    )}
                  >
                    {formatDuration(callDuration)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMinimized(true)}
                className={cn(
                  'flex-shrink-0 h-7 w-7 rounded-md flex items-center justify-center transition-colors',
                  darkMode
                    ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'
                    : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700',
                )}
                title={t('phone.minimize', 'Réduire')}
                aria-label={t('phone.minimize', 'Réduire')}
              >
                <Minus className="h-4 w-4" />
              </button>
            </div>

            <AnnonceBar darkMode={darkMode} compact />

            {/* Actions */}
            <div className="px-3 py-3">
              <div className="flex items-center justify-center gap-1.5">
                <ActionButton
                  onClick={toggleMute}
                  active={isCallMuted}
                  tone={isCallMuted ? 'destructive' : 'default'}
                  darkMode={darkMode}
                  label={
                    isCallMuted
                      ? t('phone.unmute', 'Mic off')
                      : t('phone.mute', 'Mute')
                  }
                  icon={
                    isCallMuted ? (
                      <MicOff className="w-[18px] h-[18px]" />
                    ) : (
                      <Mic className="w-[18px] h-[18px]" />
                    )
                  }
                />

                <ActionButton
                  onClick={toggleKeypad}
                  active={showKeypad}
                  darkMode={darkMode}
                  label={t('phone.keypad', 'Clavier')}
                  icon={<Grid3x3 className="w-[18px] h-[18px]" />}
                />

                <ActionButton
                  onClick={() => void (isOnHold ? unhold() : hold())}
                  active={isOnHold}
                  disabled={isHoldLoading || enAnnonce}
                  darkMode={darkMode}
                  label={
                    isOnHold
                      ? t('phone.resume', 'Reprendre')
                      : t('phone.hold', 'Pause')
                  }
                  icon={
                    isOnHold ? (
                      <Play className="w-[18px] h-[18px]" />
                    ) : (
                      <Pause className="w-[18px] h-[18px]" />
                    )
                  }
                />

                <ActionButton
                  onClick={openAddPanel}
                  active={showTransfer && ajout}
                  disabled={isOnHold || enAnnonce || !!ajoutEnCours || interne}
                  darkMode={darkMode}
                  label={t('phone.ajout.label', 'Ajouter')}
                  icon={<UserPlus className="w-[18px] h-[18px]" />}
                />

                <ActionButton
                  onClick={openTransferPanel}
                  active={showTransfer && !ajout}
                  disabled={isOnHold || enAnnonce || participants.length > 0}
                  darkMode={darkMode}
                  label={t('phone.transferLabel', 'Transfert')}
                  icon={<Repeat className="w-[18px] h-[18px]" />}
                />

                <button
                  type="button"
                  onClick={hangUp}
                  disabled={isOnHold}
                  className="flex flex-col items-center justify-center gap-0.5 bg-red-500 hover:bg-red-600 text-white rounded-xl h-[52px] w-[46px] transition-colors disabled:opacity-50 shadow-md shadow-red-500/30"
                  title={quitte ? t('phone.ajout.leaveHint', 'Les autres restent en ligne') : t('phone.hangup', 'Raccrocher')}
                >
                  <PhoneOff className="w-[18px] h-[18px]" />
                  <span className="text-[8px] font-semibold leading-none">
                    {quitte ? t('phone.ajout.leave', 'Quitter') : t('phone.hangup', 'Raccrocher')}
                  </span>
                </button>
              </div>
            </div>

            {/* Panneau de transfert inline — composant partagé (3 onglets).
                Le keypad et ce panneau s'excluent. */}
            <AnimatePresence initial={false}>
              {showTransfer && (
                <motion.div
                  key="transfer"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <div
                    className={cn(
                      'px-4 py-3 border-t space-y-2',
                      darkMode ? 'border-slate-800' : 'border-slate-100',
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={cn(
                          'text-[10px] font-semibold uppercase tracking-wider',
                          darkMode ? 'text-slate-400' : 'text-slate-500',
                        )}
                      >
                        {ajout ? t('phone.ajout.title', "Ajouter à l'appel") : t('phone.transfer.title', "Transférer l'appel")}
                      </span>
                      <button
                        type="button"
                        onClick={cancelTransfer}
                        className={cn(
                          'h-5 w-5 rounded flex items-center justify-center transition-colors',
                          darkMode
                            ? 'hover:bg-slate-800 text-slate-500 hover:text-slate-300'
                            : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600',
                        )}
                        aria-label={t('common.cancel', 'Annuler')}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <TransferPanel
                      key={ajout ? 'add' : 'transfer'}
                      darkMode={darkMode}
                      compact
                      ajout={ajout}
                      onTransferred={cancelTransfer}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Keypad DTMF */}
            <AnimatePresence initial={false}>
              {showKeypad && (
                <motion.div
                  key="keypad"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <div
                    className={cn(
                      'px-4 pb-4 pt-1 border-t',
                      darkMode ? 'border-slate-800' : 'border-slate-100',
                    )}
                  >
                    <div className="grid grid-cols-3 gap-1.5 max-w-[220px] mx-auto pt-3">
                      {[
                        '1',
                        '2',
                        '3',
                        '4',
                        '5',
                        '6',
                        '7',
                        '8',
                        '9',
                        '*',
                        '0',
                        '#',
                      ].map((digit) => (
                        <button
                          key={digit}
                          type="button"
                          onClick={() => sendDTMF(digit)}
                          className={cn(
                            'h-9 rounded-md text-sm font-semibold tabular-nums transition-colors',
                            darkMode
                              ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                              : 'bg-white hover:bg-slate-50 text-slate-900 border border-slate-200',
                          )}
                        >
                          {digit}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  )
}

interface ActionButtonProps {
  onClick: () => void
  icon: React.ReactNode
  label: string
  active?: boolean
  disabled?: boolean
  tone?: 'default' | 'destructive'
  darkMode: boolean
}

function ActionButton({
  onClick,
  icon,
  label,
  active,
  disabled,
  tone = 'default',
  darkMode,
}: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        'h-[52px] w-[46px] rounded-xl flex flex-col items-center justify-center gap-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        tone === 'destructive' && active
          ? 'bg-red-500 text-white shadow-md shadow-red-500/30'
          : active
            ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30'
            : darkMode
              ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700'
              : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 shadow-sm',
      )}
    >
      {icon}
      <span className="text-[8px] font-semibold leading-none">{label}</span>
    </button>
  )
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

function getInitials(name: string, phoneNumber: string): string {
  if (name) {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase()
  }
  return phoneNumber ? phoneNumber.slice(-2) : '?'
}

